import * as React from 'react';
import type { AuthenticationFailedAction } from '@Application/Security/Authentications/Actions/authenticationFailed';
import Login from '@Application/Security/Authentications/Commands/Login';
import login from '@Application/Security/Authentications/login';
import StartSsoAuthentication from '@Application/Security/Sso/Commands/StartSsoAuthentication';
import startSsoAuthentication from '@Application/Security/Sso/startSsoAuthentication';
import resolvePostLoginDestination from '@UI/Routing/resolvePostLoginDestination';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import LoginFormData from '@UI/Pages/Authentication/Login/FormData/LoginFormData';
import loginStepSignal, {
	goToIdentificationStep,
} from '@UI/Pages/Authentication/Login/Signals/loginStepSignal';
import AuthenticationModesView from '@UI/Store/Sso/Views/AuthenticationModesView';
import { css } from '@emotion/css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useSelector } from '@verclam/synapse';
import { Button, Form, Password } from '@verclam/ui-kit';
import { fieldMarginStyles } from '@Guidelines/Theme/commonStyles';

const emailRecapStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	margin: 0 1.25rem 1rem 1.25rem;
	padding-left: .5rem;
`;

const formContainerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin-top: 1rem;
`;

/**
 * Resolution failed and the server could not tell which modes the domain allows: the only silent
 * fallback of this screen, and a harmless one — a password-only step 2 opens, and the server will
 * reject the password anyway if the account turns out to be SSO-exclusive. Its `email` is the empty
 * string, which no submitted address can ever equal, so it is never mistaken for a real result.
 */
const FALLBACK_AUTHENTICATION_MODES = new AuthenticationModesView(
	{ passwordEnabled: true, ssoEnabled: false },
	'',
);

/**
 * Only the password is marked: the address is a read-only recap on this step, and a forced error on a
 * field the visitor cannot edit would keep the form invalid for good.
 */
const REJECTED_CREDENTIALS = [{ field: 'password', message: 'Identifiants incorrects.' }];

/** A `401` arrives with an empty body. Any other failure (403, 429, network) marks no field: the toast explains it. */
const isRejectedCredentials = (action: AuthenticationFailedAction): boolean =>
	action.payload === undefined || action.payload.status === 401;

/**
 * Step 2: composition depends entirely on `modes`. In SSO-exclusive mode, the password field is not
 * rendered at all — not hidden, not disabled. In coexistence, the password form stays hidden until the
 * visitor asks for it through a discrete text button, so the SSO button reads as the primary path.
 */
const CredentialsStep: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const step = useSignalValue(loginStepSignal);

	// Seeds from the signal: the address was captured by step 1 before the transition, so this step
	// never asks the visitor to type it twice.
	const [formData, setFormData] = React.useState(() => LoginFormData.createEmpty().setEmail(step.email));
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const [isStartingSso, setIsStartingSso] = React.useState(false);
	const [isPasswordFormVisible, setIsPasswordFormVisible] = React.useState(false);

	const storedAuthenticationModes = useSelector((state) => state.ssoStore.authenticationModes);

	// Only trust the store's modes for the address they were resolved for: `UserSidebar` resolves the
	// same action for a different address, and a stale or foreign entry must fall back rather than leak.
	const authenticationModes = React.useMemo(
		() =>
			storedAuthenticationModes?.email === formData.email
				? storedAuthenticationModes
				: FALLBACK_AUTHENTICATION_MODES,
		[formData.email, storedAuthenticationModes],
	);

	const isCoexistence = authenticationModes.ssoEnabled && authenticationModes.passwordEnabled;

	const handleChangeEmail = React.useCallback(() => {
		goToIdentificationStep(formData.email);
	}, [formData.email]);

	const handleRevealPasswordForm = React.useCallback(() => {
		setIsPasswordFormVisible(true);
	}, []);

	const handlePasswordChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPassword(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		setIsSubmitting(true);

		dispatch(login(new Login(formData.email, formData.password)))
			.then((action) => {
				if (action.type === 'USER_AUTHENTICATED') {
					navigate(resolvePostLoginDestination(location.state), { replace: true });
					return;
				}

				if (action.type === 'SECOND_FACTOR_REQUIRED') {
					navigate('/login/second-factor', { state: location.state });
					return;
				}

				// `AUTHENTICATION_FAILED` never resolves here: `login` rejects on failure (`.catch()` below).
				if (action.type === 'SECOND_FACTOR_ENROLLMENT_REQUIRED') {
					navigate('/login/enrollment', { state: location.state });
				}
			})
			.catch((action: AuthenticationFailedAction) => {
				if (!isRejectedCredentials(action)) {
					return;
				}

				updateValidationFromExternalSource({
					action: { type: action.type, payload: { fieldErrors: REJECTED_CREDENTIALS } },
					eventNames: 'AUTHENTICATION_FAILED',
					formDataUpdater: setFormData,
				});
			})
			.finally(() => setIsSubmitting(false));
	}, [dispatch, formData, location, navigate]);

	const handleStartSso = React.useCallback(() => {
		setIsStartingSso(true);

		const destination = resolvePostLoginDestination(location.state);
		const redirectPath = destination === '/' ? null : destination;

		dispatch(startSsoAuthentication(new StartSsoAuthentication(formData.email, redirectPath)))
			.catch(ignoreHandledError) // toast displayed by AuthenticationNotifications
			.finally(() => setIsStartingSso(false));
	}, [dispatch, formData.email, location]);

	return (
		<React.Fragment>
			<div className={emailRecapStyle}>
				<span>{formData.email}</span>
				<Button label="Changer d’adresse" text type="button" onClick={handleChangeEmail} />
			</div>
			{authenticationModes.ssoEnabled && (
				<Button
					type="button"
					label="Se connecter avec le compte de ma structure"
					onClick={handleStartSso}
					loading={isStartingSso}
					css={fieldMarginStyles}
				/>
			)}
			{isCoexistence && !isPasswordFormVisible ? (
				<Button
					label="J’utilise un mot de passe"
					text
					type="button"
					onClick={handleRevealPasswordForm}
					css={fieldMarginStyles}
				/>
			) : null}
			{authenticationModes.passwordEnabled && (!isCoexistence || isPasswordFormVisible) && (
				<div className={formContainerStyle}>
					<Form onSubmit={handleSubmit}>
						<Password
							name="password"
							label="Mot de passe"
							floatLabel
							feedback={false}
							toggleMask
							autoComplete="current-password"
							required
							value={formData.password}
							onChange={handlePasswordChange}
							errorMessage={formData.getValidationMessage('password')}
							isFormFieldValid={formData.isValid('password')}
						/>
						<Button
							type="submit"
							label="Se connecter"
							loading={isSubmitting}
							css={fieldMarginStyles}
						/>
					</Form>
					<Link to="/password/forgotten" css={{ alignSelf: 'flex-end', margin: '0 1.25rem' }}>
						Mot de passe oublié ?
					</Link>
				</div>
			)}
		</React.Fragment>
	);
};

export default CredentialsStep;
